import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Clock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { type ActivityType, getActivityIconByType } from '@/lib/activityIcons';

interface TimelineActivity {
  id: string;
  ticketId: string;
  type: ActivityType;
  ticketNumber: number;
  ticketSubject: string;
  description: string;
  timestamp: string;
  status?: string;
}

interface ActivityTimelineProps {
  activities: TimelineActivity[];
  isLoading?: boolean;
  onActivityClick?: (ticketId: string) => void;
}

const ITEMS_PER_PAGE = 5;

export function ActivityTimeline({ activities, isLoading = false, onActivityClick }: ActivityTimelineProps) {
  const [currentPage, setCurrentPage] = useState(1);

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Pagination
  const totalPages = Math.ceil(activities.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedActivities = activities.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (isLoading) {
    return (
      <Card className="col-span-full">
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3 pb-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  <div className="h-3 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="col-span-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
            No recent activity
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {paginatedActivities.map((activity, index) => {
                const { icon: Icon, color, bgColor } = getActivityIconByType(activity.type);

                return (
                  <div
                    key={activity.id}
                    onClick={() => onActivityClick?.(activity.ticketId)}
                    className={cn(
                      'flex gap-2.5 py-2 px-2 -mx-2 rounded-lg transition-colors',
                      onActivityClick && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50',
                      index !== paginatedActivities.length - 1 && 'border-b border-gray-100 dark:border-gray-800'
                    )}
                  >
                    {/* Icon */}
                    <div className={cn('p-1.5 rounded-md h-fit shrink-0', bgColor)}>
                      <Icon className={cn('h-3.5 w-3.5', color)} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0 h-5 shrink-0">
                            #{activity.ticketNumber}
                          </Badge>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
                            {activity.ticketSubject}
                          </span>
                        </div>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap shrink-0">
                          {formatTimeAgo(activity.timestamp)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 truncate mt-0.5">
                        {activity.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-7 px-2 text-xs"
                >
                  <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                  Prev
                </Button>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {currentPage} of {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="h-7 px-2 text-xs"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
